import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Edit2, Trash2, LogOut, Save, X } from 'lucide-react';
import config from '../../config';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingService, setEditingService] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    icon: 'shield',
    features: '',
    display_order: 0,
    status: 'active'
  });
  const navigate = useNavigate();

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${config.ADMIN_API_BASE_URL}/services.php`, {
        withCredentials: true
      });
      setServices(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching services:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    const adminUser = localStorage.getItem('admin_user');
    if (!adminUser) {
      navigate('/admin/login');
      return;
    }
    fetchServices();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await axios.get(`${config.ADMIN_API_BASE_URL}/logout.php`, {
        withCredentials: true
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleEdit = (service) => {
    setEditingService(service.id);
    setFormData({
      id: service.id,
      title: service.title,
      description: service.description,
      icon: service.icon,
      features: Array.isArray(service.features) ? service.features.join('|') : service.features,
      display_order: service.display_order,
      status: service.status
    });
    setShowAddForm(false);
  };

  const handleAdd = () => {
    setShowAddForm(true);
    setEditingService(null);
    setFormData({
      title: '',
      description: '',
      icon: 'shield',
      features: '',
      display_order: services.length + 1,
      status: 'active'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingService) {
        await axios.put(`${config.ADMIN_API_BASE_URL}/services.php`, formData, {
          withCredentials: true
        });
      } else {
        await axios.post(`${config.ADMIN_API_BASE_URL}/services.php`, formData, {
          withCredentials: true
        });
      }
      
      fetchServices();
      setEditingService(null);
      setShowAddForm(false);
      setFormData({
        title: '',
        description: '',
        icon: 'shield',
        features: '',
        display_order: 0,
        status: 'active'
      });
    } catch (error) {
      console.error('Error saving service:', error);
      alert('Failed to save service');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service?')) {
      return;
    }

    try {
      await axios.delete(`${config.ADMIN_API_BASE_URL}/services.php`, {
        data: { id },
        withCredentials: true
      });
      fetchServices();
    } catch (error) {
      console.error('Error deleting service:', error);
      alert('Failed to delete service');
    }
  };

  const handleCancel = () => {
    setEditingService(null);
    setShowAddForm(false);
    setFormData({
      title: '',
      description: '',
      icon: 'shield',
      features: '',
      display_order: 0,
      status: 'active'
    });
  };

  if (loading) {
    return <div className="loading-container">Loading...</div>;
  }

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="admin-header-content">
          <h1>Service Management</h1>
          <button onClick={handleLogout} className="btn-logout">
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </header>

      <div className="admin-content">
        <div className="admin-actions">
          <button onClick={handleAdd} className="btn-add" disabled={showAddForm}>
            <Plus size={20} />
            Add New Service
          </button>
        </div>

        {(showAddForm || editingService) && (
          <div className="service-form-container">
            <h2>{editingService ? 'Edit Service' : 'Add New Service'}</h2>
            <form onSubmit={handleSubmit} className="service-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Icon</label>
                  <select name="icon" value={formData.icon} onChange={handleChange}>
                    <option value="shield">Shield (SOC)</option>
                    <option value="network">Network (NOC)</option>
                    <option value="server">Server (Data Center)</option>
                    <option value="headset">Headset (Support)</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows="4"
                />
              </div>

              <div className="form-group">
                <label>Features (separate with |)</label>
                <textarea
                  name="features"
                  value={formData.features}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Feature 1|Feature 2|Feature 3"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Display Order</label>
                  <input
                    type="number"
                    name="display_order"
                    value={formData.display_order}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <select name="status" value={formData.status} onChange={handleChange}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-save">
                  <Save size={18} />
                  Save Service
                </button>
                <button type="button" onClick={handleCancel} className="btn-cancel">
                  <X size={18} />
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="services-table">
          <table>
            <thead>
              <tr>
                <th>Order</th>
                <th>Title</th>
                <th>Icon</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {services.map((service) => (
                <tr key={service.id}>
                  <td>{service.display_order}</td>
                  <td>{service.title}</td>
                  <td>{service.icon}</td>
                  <td>
                    <span className={`status-badge ${service.status}`}>
                      {service.status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => handleEdit(service)}
                        className="btn-edit"
                        title="Edit"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(service.id)}
                        className="btn-delete"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
